import { useCallback, useEffect, useSyncExternalStore } from 'react'

/**
 * Audio alert system for the support dashboard.
 *
 * Uses Web Audio API for reliable tone generation — no external sound files needed.
 * - Queue alert: rising two-tone chime when a new chat is queued.
 * - Message alert: short chirp when a customer sends a message in the active chat.
 *
 * Falls back to HTMLAudioElement if Web Audio API is unavailable.
 */

// Global mute state.
//
// Two independent mute sources — either one silences audio + notifications:
//   • _manualMuted — the agent's manual toggle. Persisted to localStorage so
//     it survives a page refresh (it used to reset to false on reload).
//   • _serverMuted — a server-decided signal (e.g. an active notice with
//     mute_alerts set). Lives in memory only; the server is the source of truth.
//
// `isAudioMuted()` returns true when EITHER is active. WhatsApp-sourced chats
// are handled upstream (GlobalQueueAlerts only feeds widget chats into the ring
// loop and ignores notice-mute for WhatsApp), so this layer can treat mute as
// a simple "silence everything currently ringing" switch.
const MUTE_STORAGE_KEY = 'audio_alerts_muted'

function readPersistedMute(): boolean {
  try {
    return localStorage.getItem(MUTE_STORAGE_KEY) === 'true'
  } catch {
    return false
  }
}

let _manualMuted = readPersistedMute()
let _serverMuted = false
// Dev Mode mute — a third, app-mode-driven source. While the dashboard is in
// Dev Mode, ALL audio + desktop notifications are silenced AT THE SOURCE, no
// matter which component requests them (a hard gate, not just unmounting the
// alert components). Set by AppModeContext when the mode changes; in-memory
// only (the mode itself is persisted in localStorage and re-applied on load).
let _devMuted = false

function isAudioMuted(): boolean {
  return _manualMuted || _serverMuted || _devMuted
}

/**
 * Silence (or un-silence) ALL alerts because the app entered/left Dev Mode.
 * Exported as a plain module function so the AppMode provider can call it
 * without going through the hook. Folds into isAudioMuted() alongside the
 * manual + server mutes, so every audio path — and the desktop-notification
 * gate, which checks isEffectivelyMuted — honours it.
 */
export function setAudioDevMuted(muted: boolean): void {
  if (_devMuted === muted) return
  _devMuted = muted
  notifyAlertListeners()
}

// ── "Audio blocked" state ───────────────────────────────────────────────────
// Browsers start an AudioContext SUSPENDED and only allow resume() during/after
// a user gesture. If a chime is requested before the agent has interacted with
// the page, the tone plays into a suspended context and is SILENT — with no
// error. We surface that as `audioBlocked` so the UI can show a "Click to enable
// sound" prompt, and we resume the context on the first real interaction
// (installAudioUnlock), which is the only moment the browser permits it.
let _audioBlocked = false
const _audioBlockedListeners = new Set<() => void>()
function setAudioBlocked(v: boolean) {
  if (_audioBlocked === v) return
  _audioBlocked = v
  console.info('[audio] blocked state ->', v)
  _audioBlockedListeners.forEach((l) => l())
}
function subscribeAudioBlocked(cb: () => void) {
  _audioBlockedListeners.add(cb)
  return () => { _audioBlockedListeners.delete(cb) }
}
function getAudioBlockedSnapshot() { return _audioBlocked }

let _unlockInstalled = false
/** Resume (and thereby unlock) the AudioContext on the first user gesture. */
function installAudioUnlock() {
  if (_unlockInstalled || typeof window === 'undefined') return
  _unlockInstalled = true
  const handler = () => {
    const ctx = getAudioContext()
    if (!ctx) return
    const finish = () => {
      const running = ctx.state === 'running'
      setAudioBlocked(!running)
      if (running) {
        window.removeEventListener('click', handler)
        window.removeEventListener('keydown', handler)
        window.removeEventListener('touchstart', handler)
        console.info('[audio] AudioContext unlocked on user gesture (running)')
      }
    }
    if (ctx.state === 'suspended') ctx.resume().then(finish).catch(finish)
    else finish()
  }
  window.addEventListener('click', handler)
  window.addEventListener('keydown', handler)
  window.addEventListener('touchstart', handler)
}

// Track which chat IDs are currently triggering the VISUAL queue alert (pulsing row).
// This is separate from audio cooldown below — visual state should stay on until
// the agent picks up the chat; audio should only dedup very brief duplicates.
let _alertingChatIds = new Set<string>()

// Per-chat audio cooldown. Stores the wall-clock time (ms) the sound last played
// for that chat. A repeat within AUDIO_COOLDOWN_MS is treated as a spam duplicate
// (filter switch, socket reconnect, bulk fetch) and stays silent. A repeat after
// the cooldown is treated as a legitimate new signal (customer reopened widget,
// dashboard just mounted) and plays again.
//
// The old implementation used a permanent Set keyed by chat ID — which meant a
// customer reopening their widget after a while never triggered the sound again
// because the server correctly returns the same queued chat ID. That manifested
// as "audio doesn't consistently play for new chats" on local testing.
const _lastAudioAt = new Map<string, number>()
const AUDIO_COOLDOWN_MS = 5000

// ── Continuous-ring state ────────────────────────────────────────────────
// Chats that should keep audibly ringing until an agent picks them up (or
// they close). Distinct from the one-shot playQueueAlert path above: this
// set drives a global setInterval that replays the tone every
// RING_INTERVAL_MS for as long as at least one chat is in the set. Shared
// across ALL pages — the <GlobalQueueAlerts /> component (mounted in
// Layout) feeds it by subscribing to socket events app-wide, so the sound
// is heard whether the agent is on Queue, My Chats, Customers, Settings,
// Groups, or anywhere else.
const _ringingChatIds = new Set<string>()
const RING_INTERVAL_MS = 4000
let _ringingInterval: ReturnType<typeof setInterval> | null = null

let _alertingVersion = 0
const _listeners = new Set<() => void>()

function notifyAlertListeners() {
  _alertingVersion++
  _listeners.forEach((l) => l())
}

function subscribeAlerting(cb: () => void) {
  _listeners.add(cb)
  return () => { _listeners.delete(cb) }
}

function getAlertingSnapshot() {
  return _alertingVersion
}

// ── Ring-loop helpers ──────────────────────────────────────────────────
// Declared BEFORE the tone generators so we can call playQueueTone from
// inside ensureRinging below (tone fn is hoisted by `function` anyway, but
// keeping the ordering explicit for readability).

function ensureRinging() {
  if (_ringingInterval) return
  // Play once immediately for perceived responsiveness, then continue on
  // the interval.
  if (!isAudioMuted() && _ringingChatIds.size > 0) playQueueTone()
  _ringingInterval = setInterval(() => {
    if (isAudioMuted() || _ringingChatIds.size === 0) return
    playQueueTone()
  }, RING_INTERVAL_MS)
}

function stopRingingLoop() {
  if (_ringingInterval) {
    clearInterval(_ringingInterval)
    _ringingInterval = null
  }
}

// ─── Web Audio API tone generation ──────────────────────────────────────────

let _audioCtx: AudioContext | null = null

function getAudioContext(): AudioContext | null {
  try {
    if (!_audioCtx || _audioCtx.state === 'closed') {
      _audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)()
    }
    // Resume if suspended (autoplay policy). Calling resume() here (outside a
    // user gesture) usually does NOT unlock — so if we're still suspended, flag
    // audio as blocked so the UI can prompt for a click. The gesture listener in
    // installAudioUnlock() is what actually resumes it.
    if (_audioCtx.state === 'suspended') {
      _audioCtx.resume().catch(() => {})
      setAudioBlocked(true)
    } else if (_audioCtx.state === 'running') {
      setAudioBlocked(false)
    }
    return _audioCtx
  } catch {
    return null
  }
}

/**
 * Play a rising two-tone chime for new queued chats.
 * First tone: 523 Hz (C5) for 200ms, second tone: 659 Hz (E5) for 300ms.
 */
function playQueueTone(): boolean {
  const ctx = getAudioContext()
  if (!ctx) return false

  try {
    const now = ctx.currentTime

    // First tone: C5 (523 Hz)
    const osc1 = ctx.createOscillator()
    const gain1 = ctx.createGain()
    osc1.type = 'sine'
    osc1.frequency.setValueAtTime(523.25, now)
    gain1.gain.setValueAtTime(0, now)
    gain1.gain.linearRampToValueAtTime(0.4, now + 0.02)
    gain1.gain.linearRampToValueAtTime(0.35, now + 0.15)
    gain1.gain.linearRampToValueAtTime(0, now + 0.25)
    osc1.connect(gain1)
    gain1.connect(ctx.destination)
    osc1.start(now)
    osc1.stop(now + 0.25)

    // Second tone: E5 (659 Hz) — starts slightly after first
    const osc2 = ctx.createOscillator()
    const gain2 = ctx.createGain()
    osc2.type = 'sine'
    osc2.frequency.setValueAtTime(659.25, now + 0.2)
    gain2.gain.setValueAtTime(0, now + 0.2)
    gain2.gain.linearRampToValueAtTime(0.45, now + 0.22)
    gain2.gain.linearRampToValueAtTime(0.4, now + 0.4)
    gain2.gain.linearRampToValueAtTime(0, now + 0.6)
    osc2.connect(gain2)
    gain2.connect(ctx.destination)
    osc2.start(now + 0.2)
    osc2.stop(now + 0.6)

    // Third tone: G5 (784 Hz) — completes the triad
    const osc3 = ctx.createOscillator()
    const gain3 = ctx.createGain()
    osc3.type = 'sine'
    osc3.frequency.setValueAtTime(783.99, now + 0.45)
    gain3.gain.setValueAtTime(0, now + 0.45)
    gain3.gain.linearRampToValueAtTime(0.4, now + 0.47)
    gain3.gain.linearRampToValueAtTime(0.35, now + 0.7)
    gain3.gain.linearRampToValueAtTime(0, now + 1.0)
    osc3.connect(gain3)
    gain3.connect(ctx.destination)
    osc3.start(now + 0.45)
    osc3.stop(now + 1.0)

    return true
  } catch {
    return false
  }
}

/**
 * Play a short chirp for incoming customer messages.
 * Single tone: 880 Hz (A5) for 120ms with quick fade.
 */
function playMessageTone(): boolean {
  const ctx = getAudioContext()
  if (!ctx) return false

  try {
    const now = ctx.currentTime
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.type = 'sine'
    osc.frequency.setValueAtTime(880, now)
    osc.frequency.linearRampToValueAtTime(1046.5, now + 0.08) // slide up to C6
    gain.gain.setValueAtTime(0, now)
    gain.gain.linearRampToValueAtTime(0.35, now + 0.01)
    gain.gain.linearRampToValueAtTime(0.3, now + 0.08)
    gain.gain.linearRampToValueAtTime(0, now + 0.15)
    osc.connect(gain)
    gain.connect(ctx.destination)
    osc.start(now)
    osc.stop(now + 0.15)
    return true
  } catch {
    return false
  }
}

// ─── Fallback: HTMLAudioElement (used if Web Audio API fails) ───────────────

let longTone: HTMLAudioElement | null = null
let shortTone: HTMLAudioElement | null = null

function getLongToneFallback(): HTMLAudioElement {
  if (!longTone) {
    longTone = new Audio('/sounds/long-tone.wav')
    longTone.loop = false
    longTone.volume = 0.7
  }
  return longTone
}

function getShortToneFallback(): HTMLAudioElement {
  if (!shortTone) {
    shortTone = new Audio('/sounds/short-tone.wav')
    shortTone.loop = false
    shortTone.volume = 0.6
  }
  return shortTone
}

// ─── Hook ───────────────────────────────────────────────────────────────────

export const useAudioAlerts = () => {
  // Subscribe to alerting chat changes so components re-render
  useSyncExternalStore(subscribeAlerting, getAlertingSnapshot)
  // Re-render when the audio-blocked state flips (so the "enable sound" prompt
  // appears/disappears), and install the one-time gesture unlock.
  const audioBlocked = useSyncExternalStore(subscribeAudioBlocked, getAudioBlockedSnapshot)
  useEffect(() => { installAudioUnlock() }, [])

  /**
   * Manually resume the AudioContext (called by the "Click to enable sound"
   * affordance). Because the click IS a user gesture, resume() succeeds here.
   */
  const enableAudio = useCallback(() => {
    const ctx = getAudioContext()
    if (!ctx) return
    if (ctx.state === 'suspended') {
      ctx.resume().then(() => setAudioBlocked(ctx.state !== 'running')).catch(() => {})
    } else {
      setAudioBlocked(false)
    }
  }, [])

  /** Play the queue alert tone for a new queued chat */
  const playQueueAlert = useCallback((chatId?: string) => {
    // Always ensure the visual pulse shows for this chat (idempotent).
    if (chatId) {
      const wasAlerting = _alertingChatIds.has(chatId)
      if (!wasAlerting) {
        _alertingChatIds.add(chatId)
        notifyAlertListeners()
      }
    }
    if (isAudioMuted()) return

    // Audio cooldown: skip if this chat's sound played within the last
    // AUDIO_COOLDOWN_MS. Fresh chats (never seen) and chats whose cooldown
    // has elapsed both play normally.
    if (chatId) {
      const lastAt = _lastAudioAt.get(chatId) || 0
      if (Date.now() - lastAt < AUDIO_COOLDOWN_MS) return
      _lastAudioAt.set(chatId, Date.now())
    }

    // Try Web Audio API first, fall back to HTMLAudioElement
    if (!playQueueTone()) {
      try {
        const audio = getLongToneFallback()
        audio.currentTime = 0
        audio.play().catch(() => {})
      } catch {
        // Audio not available
      }
    }
  }, [])

  /** Stop the alert visual indicator (agent answered the chat) */
  const stopQueueAlert = useCallback((chatId?: string) => {
    if (chatId) {
      _alertingChatIds.delete(chatId)
      _ringingChatIds.delete(chatId)
      // Also clear the audio cooldown so a subsequent re-queue (e.g. chat
      // was reopened after being closed) makes the sound immediately.
      _lastAudioAt.delete(chatId)
    } else {
      _alertingChatIds.clear()
      _ringingChatIds.clear()
      _lastAudioAt.clear()
    }
    if (_ringingChatIds.size === 0) stopRingingLoop()
    notifyAlertListeners()
  }, [])

  /**
   * Begin (or continue) ringing for a chat. Unlike playQueueAlert — which
   * fires a single tone with a short cooldown — this adds the chat to the
   * ring-loop set so the tone keeps playing every RING_INTERVAL_MS until
   * stopRinging(chatId) is called (usually when an agent picks it up).
   *
   * Called by <GlobalQueueAlerts /> in response to socket `new_chat` events,
   * so the sound is heard regardless of which page the agent is viewing.
   */
  const startRinging = useCallback((chatId: string) => {
    if (!chatId) return
    const wasPresent = _ringingChatIds.has(chatId)
    _ringingChatIds.add(chatId)
    _alertingChatIds.add(chatId) // mirror into the visual set
    if (!wasPresent) notifyAlertListeners()
    ensureRinging()
  }, [])

  /**
   * Remove a chat from the ring-loop set. When the set becomes empty the
   * interval is cleared so we aren't burning a timer forever.
   */
  const stopRinging = useCallback((chatId: string) => {
    if (!chatId) return
    const existed = _ringingChatIds.delete(chatId)
    _alertingChatIds.delete(chatId)
    _lastAudioAt.delete(chatId)
    if (existed) notifyAlertListeners()
    if (_ringingChatIds.size === 0) stopRingingLoop()
  }, [])

  /**
   * Reconcile the ring-loop set against the canonical truth (a fresh
   * /queue?status=queued fetch). Any chat in `chatIds` that isn't already
   * ringing is added; any chat currently ringing that isn't in `chatIds` is
   * stopped. Without this reconciler, IDs added via socket / seed never get
   * removed when the chat is closed out-of-band (e.g. via direct DB cleanup,
   * via another agent on a different host, or after a session restart). That
   * was the cause of "the alert keeps ringing even though the queue is
   * empty" reported on 2026-05-06.
   *
   * Pass an empty array to silence everything (also called when the queue
   * fetch 401s, so a logged-out session doesn't keep ringing).
   */
  const setRingingChats = useCallback((chatIds: string[]) => {
    const next = new Set(chatIds.map(String))
    let changed = false

    // Remove chats no longer in the queue
    for (const id of Array.from(_ringingChatIds)) {
      if (!next.has(id)) {
        _ringingChatIds.delete(id)
        _alertingChatIds.delete(id)
        _lastAudioAt.delete(id)
        changed = true
      }
    }
    // Add new chats
    for (const id of next) {
      if (!_ringingChatIds.has(id)) {
        _ringingChatIds.add(id)
        _alertingChatIds.add(id)
        changed = true
      }
    }

    if (changed) notifyAlertListeners()
    if (_ringingChatIds.size === 0) {
      stopRingingLoop()
    } else {
      ensureRinging()
    }
  }, [])

  /** Play the message alert tone for an incoming customer message */
  const playMessageAlert = useCallback(() => {
    if (isAudioMuted()) return

    // Try Web Audio API first, fall back to HTMLAudioElement
    if (!playMessageTone()) {
      try {
        const audio = getShortToneFallback()
        audio.currentTime = 0
        audio.play().catch(() => {})
      } catch {
        // Audio not available
      }
    }
  }, [])

  /** Play the DISTINCT pickup ding — someone picked up a chat (Lee's
   *  "Correct Ding" asset). Respects the same mute sources as every other
   *  alert sound (manual mute, server/notice mute, Dev Mode). */
  const playPickupAlert = useCallback(() => {
    if (isAudioMuted()) return
    try {
      const audio = new Audio('/sounds/pickup-ding.mp3')
      audio.volume = 0.8
      audio.play().catch(() => {})
    } catch {
      // Audio not available
    }
  }, [])

  /** Check if a specific chat is currently alerting */
  const isAlertingChat = useCallback((chatId: string) => {
    return _alertingChatIds.has(chatId)
  }, [])

  /**
   * Toggle the agent's MANUAL mute on/off. Persisted to localStorage so the
   * choice survives a page refresh.
   */
  const toggleMute = useCallback(() => {
    _manualMuted = !_manualMuted
    try {
      localStorage.setItem(MUTE_STORAGE_KEY, _manualMuted ? 'true' : 'false')
    } catch {
      // localStorage unavailable (private mode / quota) — in-memory only.
    }
    notifyAlertListeners() // trigger re-render so button icon updates
  }, [])

  /**
   * Apply the server-decided mute signal. Independent of the manual toggle —
   * either source silences. Called when an active notice (or similar) sets
   * mute_alerts server-side.
   */
  const setServerMuted = useCallback((muted: boolean) => {
    if (_serverMuted === muted) return
    _serverMuted = muted
    notifyAlertListeners()
  }, [])

  /**
   * Whether the manual toggle is on. Mirrors the button's visual state — the
   * agent should see their own toggle reflected, not the server mute.
   */
  const isMuted = _manualMuted

  /** Whether audio is currently silenced by EITHER source. */
  const isEffectivelyMuted = isAudioMuted()

  return {
    playQueueAlert,
    stopQueueAlert,
    playMessageAlert,
    playPickupAlert,
    isAlertingChat,
    alertingChatIds: _alertingChatIds,
    toggleMute,
    isMuted,
    setServerMuted,
    isEffectivelyMuted,
    startRinging,
    stopRinging,
    setRingingChats,
    audioBlocked,
    enableAudio,
  }
}
